import React, { HTMLProps, useEffect, useRef, useState } from 'react'
import ArrowNavigation from '../ArrowNavigation'
import { Color } from '../../utils/constants'
import HorizontalScroll from '../HorizontalScroll'
import Step, { StepType } from './Step'
import { twMerge } from 'tailwind-merge'
import { Waypoint } from 'react-waypoint'
import StepIndicator from './StepIndicator'

interface ScrollableStepsProps extends HTMLProps<HTMLDivElement> {
  steps: StepType[]
  padding?: number
  setIsVisible: (value: boolean) => void
  color: Color
}

const ScrollableSteps = ({
    steps,
    padding,
    className,
    setIsVisible,
    color,
}: ScrollableStepsProps) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const scrollbarRef = useRef<HTMLDivElement>(null)
    const cursorRef = useRef<HTMLDivElement>(null)

    const [lastScrolledIndex, setLastScrolledIndex] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    useEffect(() => {
        const roundByEqualValue = (value: number) => {
            const part = steps.length - 1
            return Math.round(value * part) / part
        }

        const scrollHandler = () => {
            // @ts-ignore
            const { scrollLeft: scrl, scrollWidth, clientWidth } = scrollRef.current
            const percentage = roundByEqualValue(scrl / (scrollWidth - clientWidth))
            setScrollLeft(
                // @ts-ignore
                percentage * scrollbarRef.current?.clientWidth - cursorRef.current.offsetWidth * percentage,
            )
            setLastScrolledIndex((steps.length - 1) * percentage)
        }
        if (scrollRef.current) {
            scrollRef.current.addEventListener('scroll', scrollHandler)
        }
    }, [scrollRef, steps.length])

    return (
        <div className={twMerge('pb-8 md:pb-24 relative z-10', className)}>
            <div className='side-padding p-0'>
                <div
                    className='pt-12 md:pt-0 px-6 flex items-center justify-between md:block'
                    style={{ paddingLeft: padding }}
                >
                    <ArrowNavigation scrollRef={scrollRef} buttonColor={Color.BLACK} />
                </div>
                <Waypoint onEnter={() => setIsVisible(true)} onLeave={() => setIsVisible(false)}>
                    <div />
                </Waypoint>
                <HorizontalScroll
                    wrapperClasses='md:pr-24 py-12 side-padding pr-0 scroll-pl-6 gap-x-6 md:gap-x-24'
                    ref={scrollRef}
                    style={{ paddingLeft: padding, scrollPaddingLeft: padding }}
                >
                    {steps.map((step, index) => (
                        <Step
                            item={step}
                            key={step.id}
                            className={twMerge(index !== lastScrolledIndex ? 'opacity-20' : 'opacity-100')}
                        />
                    ))}
                </HorizontalScroll>
            </div>
            <div
                className='flex items-center side-padding pt-8'
                style={{ paddingLeft: padding, paddingRight: padding }}
            >
                <div className='flex items-center w-full relative'>
                    <div className='flex justify-between w-full mx-5 relative' ref={scrollbarRef}>
                        {steps.map(({ slug }, index) => (
                            <StepIndicator
                                lastScrolledIndex={lastScrolledIndex}
                                index={index}
                                key={`scroll-${slug}`}
                                color={color}
                                slug={slug}
                                scrollRef={scrollRef}
                            />
                        ))}
                        <div
                            ref={cursorRef}
                            className='w-2 h-2 border-2 border-black rounded-full bg-black absolute z-20 transition duration-500'
                            style={{ transform: `translateX(${scrollLeft}px)` }}
                        />
                    </div>
                    <div className='h-px w-full bg-black/20 absolute' />
                </div>
            </div>
        </div>
    )
}

export default ScrollableSteps
